import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import type {
  ApSummary,
  EstimateResult,
  Floor,
  ReferencePoint,
  StoredFingerprint,
} from '@wrp/shared';
import * as api from './api';
import { ApiError, STREAM_URL } from './api';

export type ToastKind = 'error' | 'info' | 'success';

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

export interface EstimateState {
  reportId: string;
  result: EstimateResult;
  at: string;
}

export type StreamEventType = 'report' | 'fingerprint' | 'reference_point';

type StreamHandler = (payload: unknown) => void;

export type ConnectionState = 'connecting' | 'open' | 'closed';

export interface AppStore {
  toasts: Toast[];
  pushToast: (kind: ToastKind, message: string) => void;
  dismissToast: (id: number) => void;
  reportError: (error: unknown, context: string) => void;

  floors: Floor[];
  floorsLoaded: boolean;
  selectedFloorId: string | null;
  selectFloor: (id: string | null) => void;
  selectedFloor: Floor | null;
  refreshFloors: () => Promise<void>;

  referencePoints: ReferencePoint[];
  refreshReferencePoints: () => Promise<void>;

  aps: ApSummary[];
  refreshAps: () => Promise<void>;

  fingerprints: StoredFingerprint[];
  refreshFingerprints: () => Promise<void>;
  fingerprintCountByRefPoint: Record<string, number>;

  estimate: EstimateState | null;
  setEstimate: (value: EstimateState | null) => void;

  highlightedFingerprintId: string | null;
  setHighlightedFingerprintId: (id: string | null) => void;

  connection: ConnectionState;
  subscribe: (type: StreamEventType, handler: StreamHandler) => () => void;
}

const AppContext = createContext<AppStore | null>(null);

export function useApp(): AppStore {
  const store = useContext(AppContext);
  if (!store) throw new Error('useApp must be used inside <AppProvider>');
  return store;
}

/** Subscribes to a named SSE event for the lifetime of the component. */
export function useStreamEvent<T>(type: StreamEventType, handler: (payload: T) => void): void {
  const { subscribe } = useApp();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(
    () => subscribe(type, (payload) => ref.current(payload as T)),
    [subscribe, type],
  );
}

let toastSeq = 0;

export function AppProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [floors, setFloors] = useState<Floor[]>([]);
  const [floorsLoaded, setFloorsLoaded] = useState(false);
  const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null);
  const [referencePoints, setReferencePoints] = useState<ReferencePoint[]>([]);
  const [aps, setAps] = useState<ApSummary[]>([]);
  const [fingerprints, setFingerprints] = useState<StoredFingerprint[]>([]);
  const [estimate, setEstimate] = useState<EstimateState | null>(null);
  const [highlightedFingerprintId, setHighlightedFingerprintId] = useState<string | null>(null);
  const [connection, setConnection] = useState<ConnectionState>('connecting');

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const pushToast = useCallback(
    (kind: ToastKind, message: string) => {
      const id = ++toastSeq;
      setToasts((prev) => [...prev.slice(-4), { id, kind, message }]);
      window.setTimeout(() => dismissToast(id), kind === 'error' ? 9000 : 4000);
    },
    [dismissToast],
  );

  const reportError = useCallback(
    (error: unknown, context: string) => {
      let message: string;
      if (error instanceof ApiError) {
        message = error.status ? `${context}: ${error.message} (HTTP ${error.status})` : `${context}: ${error.message}`;
      } else if (error instanceof Error) {
        message = `${context}: ${error.message}`;
      } else {
        message = `${context}: unexpected error`;
      }
      console.error(context, error);
      pushToast('error', message);
    },
    [pushToast],
  );

  const refreshFloors = useCallback(async () => {
    try {
      const list = await api.listFloors();
      setFloors(list);
      setSelectedFloorId((current) => {
        if (current && list.some((f) => f.id === current)) return current;
        return list[0]?.id ?? null;
      });
    } catch (error) {
      reportError(error, 'Failed to load floors');
    } finally {
      setFloorsLoaded(true);
    }
  }, [reportError]);

  const refreshReferencePoints = useCallback(async () => {
    try {
      setReferencePoints(await api.listReferencePoints());
    } catch (error) {
      reportError(error, 'Failed to load reference points');
    }
  }, [reportError]);

  const refreshAps = useCallback(async () => {
    try {
      setAps(await api.listAps());
    } catch (error) {
      reportError(error, 'Failed to load access points');
    }
  }, [reportError]);

  const refreshFingerprints = useCallback(async () => {
    try {
      setFingerprints(await api.listFingerprints());
    } catch (error) {
      reportError(error, 'Failed to load fingerprints');
    }
  }, [reportError]);

  useEffect(() => {
    void refreshFloors();
    void refreshReferencePoints();
    void refreshAps();
    void refreshFingerprints();
  }, [refreshFloors, refreshReferencePoints, refreshAps, refreshFingerprints]);

  // ------------------------------------------------------------------ stream
  const handlersRef = useRef<Map<StreamEventType, Set<StreamHandler>>>(new Map());

  const subscribe = useCallback((type: StreamEventType, handler: StreamHandler) => {
    const map = handlersRef.current;
    let set = map.get(type);
    if (!set) {
      set = new Set();
      map.set(type, set);
    }
    set.add(handler);
    return () => {
      set?.delete(handler);
    };
  }, []);

  useEffect(() => {
    const source = new EventSource(STREAM_URL);
    const dispatch = (type: StreamEventType) => (event: MessageEvent<string>) => {
      let payload: unknown;
      try {
        payload = JSON.parse(event.data) as unknown;
      } catch {
        return;
      }
      for (const handler of handlersRef.current.get(type) ?? []) handler(payload);
    };

    const onReport = dispatch('report');
    const onFingerprint = dispatch('fingerprint');
    const onReferencePoint = dispatch('reference_point');

    source.addEventListener('report', onReport as EventListener);
    source.addEventListener('fingerprint', onFingerprint as EventListener);
    source.addEventListener('reference_point', onReferencePoint as EventListener);
    source.onopen = () => {
      setConnection('open');
      void refreshFingerprints();
    };
    source.onerror = () => setConnection('closed');

    return () => {
      source.removeEventListener('report', onReport as EventListener);
      source.removeEventListener('fingerprint', onFingerprint as EventListener);
      source.removeEventListener('reference_point', onReferencePoint as EventListener);
      source.close();
    };
  }, [refreshFingerprints]);

  // Keep derived collections fresh when the server pushes new entities.
  useStreamEventInternal(subscribe, 'fingerprint', (payload: StoredFingerprint) => {
    setFingerprints((prev) => [payload, ...prev.filter((f) => f.id !== payload.id)]);
  });
  // A labeled report resolves to a fingerprint server-side; re-sync so the reference point flips to completed
  // even if the fingerprint event was missed.
  useStreamEventInternal(subscribe, 'report', (payload: { ground_truth?: unknown }) => {
    if (payload.ground_truth && typeof payload.ground_truth === 'object' && 'label' in payload.ground_truth) {
      void refreshFingerprints();
    }
  });
  useStreamEventInternal(subscribe, 'reference_point', (payload: ReferencePoint) => {
    setReferencePoints((prev) =>
      prev.some((p) => p.id === payload.id)
        ? prev.map((p) => (p.id === payload.id ? payload : p))
        : [...prev, payload],
    );
  });

  const selectFloor = useCallback((id: string | null) => setSelectedFloorId(id), []);

  const selectedFloor = useMemo(
    () => floors.find((f) => f.id === selectedFloorId) ?? null,
    [floors, selectedFloorId],
  );

  const fingerprintCountByRefPoint = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const fp of fingerprints) {
      if (!fp.reference_point_id) continue;
      counts[fp.reference_point_id] = (counts[fp.reference_point_id] ?? 0) + 1;
    }
    return counts;
  }, [fingerprints]);

  const value = useMemo<AppStore>(
    () => ({
      toasts,
      pushToast,
      dismissToast,
      reportError,
      floors,
      floorsLoaded,
      selectedFloorId,
      selectFloor,
      selectedFloor,
      refreshFloors,
      referencePoints,
      refreshReferencePoints,
      aps,
      refreshAps,
      fingerprints,
      refreshFingerprints,
      fingerprintCountByRefPoint,
      estimate,
      setEstimate,
      highlightedFingerprintId,
      setHighlightedFingerprintId,
      connection,
      subscribe,
    }),
    [
      toasts,
      pushToast,
      dismissToast,
      reportError,
      floors,
      floorsLoaded,
      selectedFloorId,
      selectFloor,
      selectedFloor,
      refreshFloors,
      referencePoints,
      refreshReferencePoints,
      aps,
      refreshAps,
      fingerprints,
      refreshFingerprints,
      fingerprintCountByRefPoint,
      estimate,
      highlightedFingerprintId,
      connection,
      subscribe,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

/** Same as useStreamEvent but usable inside the provider itself. */
function useStreamEventInternal<T>(
  subscribe: AppStore['subscribe'],
  type: StreamEventType,
  handler: (payload: T) => void,
): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(
    () => subscribe(type, (payload) => ref.current(payload as T)),
    [subscribe, type],
  );
}

/** Convenience wrapper: runs an async action and routes failures to a toast. */
export function useSafeAction(): <T>(
  context: string,
  action: () => Promise<T>,
) => Promise<T | undefined> {
  const { reportError } = useApp();
  return useCallback(
    async <T,>(context: string, action: () => Promise<T>): Promise<T | undefined> => {
      try {
        return await action();
      } catch (error) {
        reportError(error, context);
        return undefined;
      }
    },
    [reportError],
  );
}
